// HR-Modul v2 — Typen (Finanzen + IPB + Scopes), plans/hr-v2.md Phase 8.1

export type PayoutKind = 'paid_out' | 'lesson_account'
export type DeductionKind = 'ipb' | 'salary'
export type PayoutSource = 'self_choice' | 'default' | 'override' | 'hr'

export interface HrCaseLesson {
  id: string
  date: string
  slot: string | null
  subject_name: string
  assigned_teacher_id: string | null
  assigned_teacher_name: string
  self_choice: PayoutKind | null
  default_payout: PayoutKind
  payout_source: PayoutSource
}

export interface HrCaseSubjectGroup {
  subject_id: string | null
  subject_name: string
  lesson_ids: string[]
  default_payout: PayoutKind
  payout_source: PayoutSource
}

export interface HrCaseWarning {
  teacher: string
  status: 'ok' | 'warn' | 'block'
  saldo: number
  limit: number
  prospective: number
}

export interface HrFinanceCase {
  substitution_request_id: string
  earliest_lesson_date: string
  open_lessons: number
  subject_groups: HrCaseSubjectGroup[]
  lessons: HrCaseLesson[]
  requester_teacher_id: string | null
  requester_name: string
  substitutes: string[]
  absence_type: string | null
  /** Direktorkonfig v2 (D10): Label-Snapshot — Anzeige vorrangig vor Key-Map */
  absence_type_label?: string | null
  payer: 'private' | 'school'
  requester_deduction: DeductionKind | null
  open_choice: boolean
  decider_substitute: string
  warnings: HrCaseWarning[]
}

export interface HrFinanceCasesResponse {
  cases: HrFinanceCase[]
  total: number
  limit: number
  offset: number
}

export interface HrFinanceKpis {
  open_cases: number
  waiting_for_choice: number
  settled_today: number
  open_payout_lessons: number
}

export interface HrSettleOverride {
  lesson_id?: string
  subject_id?: string | null
  payout?: PayoutKind
  requester_deduction?: DeductionKind
  cost_bearer_id?: string
  amount?: number
  note?: string
}

export type HrLessonRowStatus = 'default' | 'wahl' | 'override' | 'limit'

export interface HrFinanceLessonRow {
  lesson_id: string
  substitution_request_id: string
  date: string
  start_time: string | null
  end_time: string | null
  slot: string | null
  subject_name: string
  class_name: string
  substitute_id: string | null
  substitute_name: string
  substitute_code?: string | null
  requester_id: string | null
  requester_name: string
  requester_code?: string | null
  substitute_saldo: number
  default_payout: PayoutKind
  self_choice: PayoutKind | null
  payout_source: PayoutSource
  requester_deduction: DeductionKind | null
  payer: 'private' | 'school'
  absence_type: string | null
  absence_type_label?: string | null
  amount_default: number
  status: HrLessonRowStatus
  warning: HrCaseWarning | null
  role?: 'substitute' | 'requester'
}

export interface HrLessonsResponse {
  lessons: HrFinanceLessonRow[]
  total: number
  limit: number
  offset: number
}

export interface HrLessonSettleItem {
  lesson_id: string
  payout?: PayoutKind
  requester_deduction?: DeductionKind
  cost_bearer_id?: string
  amount?: number
  note?: string
}

export interface HrLessonsSettleResult {
  success: { request_id: string; settled_lessons: number; lesson_ids?: string[] }[]
  skipped: { request_id?: string; lesson_ids?: string[]; reason: string; detail: string }[]
  failed: { request_id?: string; lesson_ids?: string[]; reason: string; detail: string }[]
  settled_lessons: number
}

export interface HrDeskTeacher {
  teacher_id: string
  teacher_name: string
  teacher_code: string | null
  saldo: number
  period_delta: number
  payout_lessons: number
  open_count: number
  open_as_substitute: number
  open_as_requester: number
  self_choice: boolean
  payout: boolean
  negative_limit: boolean
}

export interface HrDeskListResponse {
  teachers: HrDeskTeacher[]
  total: number
}

export interface HrDeskTeacherDetail extends HrDeskTeacher {
  open_lessons: HrFinanceLessonRow[]
  recent_transactions: HrStatementTx[]
}

export interface HrSettleResult {
  substitution_request_id: string
  settled_lessons: number
  tx_sums: Record<string, number>
  force_used: boolean
}

export interface HrBulkSettleResult {
  success: { request_id: string; settled_lessons: number }[]
  skipped: { request_id: string; reason: string; detail: string }[]
  failed: { request_id: string; reason: string; detail: string }[]
}

export interface HrRemindResult {
  sent: string[]
  no_account: string[]
}

// --- IPB & Auszahlungen (Seite B) ---

export interface HrSaldoRow {
  teacher_id: string
  teacher_name: string
  teacher_code: string | null
  saldo: number
  period_delta: number
  payout_lessons: number
  negative_warning?: boolean
}

export interface HrSaldenResponse {
  salden: HrSaldoRow[]
  accounting_mode: 'cumulative' | 'periodic'
  negative_saldo_behavior: 'limit' | 'warn' | 'cumulative'
  negative_saldo_limit: number
}

export interface HrIpbImportMatched {
  teacher_id: string
  teacher_code: string | null
  teacher_name: string
  current_saldo: number
  import_saldo: number
  delta: number
}

export interface HrIpbImportUnmatched {
  row: number
  teacher_code: string
  email: string
  reason: string
}

export interface HrIpbImportPreview {
  matched: HrIpbImportMatched[]
  unmatched: HrIpbImportUnmatched[]
  changed: number
  unchanged: number
}

export interface HrIpbImportApplyResult {
  applied: { teacher_id: string; teacher_name: string; delta: number; new_saldo: number }[]
  skipped: { teacher_id: string; reason: string }[]
}

export interface HrStatementTx {
  id: string
  created_at: string | null
  amount: number
  tx_type: 'credit' | 'debit' | 'period_close' | 'manual'
  source: string
  reason: string | null
  subject_name: string | null
  cost_bearer_name: string | null
  period_key: string | null
  substitution_request_id: string | null
  running_saldo: number
}

export interface HrStatementResponse {
  teacher_id: string
  teacher_name: string
  teacher_code: string | null
  saldo: number
  transactions: HrStatementTx[]
}

export interface HrPeriodInfo {
  key: string
  name: string
  type: 'school_year' | 'semester' | 'custom'
  start: string
  end: string
}

export interface HrPeriodCloseResult {
  period_key: string
  closed_teachers: { teacher_id: string; teacher_name: string; closed_amount: number }[]
  open_cases_remaining: number
}

// --- Kostenträger ---

export interface CostBearerRow {
  id: string
  name: string
  code: string
  description: string | null
  is_system: boolean
  is_active: boolean
  sort_order: number
}

// --- Scopes & Settings ---

export interface HrScope {
  key: string
  kind: 'module' | 'setting'
  label: string
  module_active: boolean
  self_activation: boolean
  visible: boolean
  editable: boolean
}

export interface HrScopesResponse {
  module_enabled: boolean
  director_enabled?: boolean
  scopes: HrScope[]
  pages: { finance_queue: boolean; ipb_payouts: boolean; statistics: boolean }
}

// hr2-v2 (E3/K6): registry-getriebene HR-Einstellungsdaten — gemeinsamer Typ
// für /hr/settings (HR-Ansicht) und den Superadmin-Preview-Adapter (GET
// /config/hr), damit die Vorschau nicht von der echten Ansicht driften kann.
export interface HrSettingsField {
  key: string
  label: string
  description: string
  type: 'int' | 'float' | 'bool' | 'str' | 'json_list' | 'enum_list' | 'slider' | 'password' | 'color'
  default: unknown
  min_value: number | null
  max_value: number | null
  choices: string[] | null
  choice_labels?: Record<string, string> | null
  delegatable: boolean
  editable: boolean
}

export interface HrSettingsCategory {
  name: string
  leitfrage: string
  intro: string
  keys: string[]
}

export interface HrSettingsData {
  values: Record<string, unknown>
  fields: HrSettingsField[]
  categories: HrSettingsCategory[]
}

export const SALARY_EXPORT_FIELDS: { key: string; label: string }[] = [
  { key: 'teacher_name', label: 'Lehrer' },
  { key: 'teacher_code', label: 'Kürzel' },
  { key: 'teacher_email', label: 'E-Mail' },
  { key: 'faculty', label: 'Fakultät' },
  { key: 'payout_lessons', label: 'Auszahlungs-Lektionen' },
  { key: 'ipb_credits', label: 'IPB-Gutschriften (Lektionen)' },
  { key: 'saldo', label: 'IPB-Saldo (Lektionen)' },
  { key: 'period', label: 'Periode' },
  { key: 'cost_bearer_split', label: 'Kostenträger' },
]
